/**
 * Checks whether `query` matches `text`. Each space-separated query token must
 * either be a substring of `text`, or be consumable as runs of consecutive
 * characters where every run starts at the beginning of a word in `text`.
 * Both strings are compared case-insensitively.
 *
 * @example
 * fuzzyMatch('Reverse - Hazards - Version', 'reversehaz')      // true (word prefixes)
 * fuzzyMatch('First Name', 'fn')                               // true (initials)
 * fuzzyMatch('First Name', 'first name')                       // true
 * fuzzyMatch('demo_adrian', 'drian')                           // true (substring)
 * fuzzyMatch('Status', 'uts')                                  // false (not a substring, out of order)
 * fuzzyMatch('working at height advanced', 'white')            // false (scattered characters)
 */
export const fuzzyMatch = (text: string, query: string): boolean => {
  const normalizedText = text.toLowerCase();
  const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);

  return tokens.every((token) => normalizedText.includes(token) || matchToken(normalizedText, token));
};

/** A position is a word start if it is the first character or follows a character that is not a letter or digit. */
const isWordStart = (text: string, index: number): boolean => {
  return index === 0 || !/[\p{L}\p{N}]/u.test(text[index - 1]);
};

/**
 * Matches `token` against `text`. A match begins at a word start and consumes
 * consecutive characters; the remainder of the token may continue at the start
 * of a later word (backtracking over candidate words). Visited (text, token)
 * index pairs are memoized so backtracking stays polynomial.
 */
const matchToken = (text: string, token: string): boolean => {
  const visited = new Set<number>();

  const matchFrom = (fromIndex: number, tokenIndex: number): boolean => {
    const key = fromIndex * token.length + tokenIndex;
    if (visited.has(key)) return false;
    visited.add(key);

    for (let start = fromIndex; start < text.length; start++) {
      if (text[start] !== token[tokenIndex] || !isWordStart(text, start)) continue;

      let length = 1;
      while (true) {
        if (tokenIndex + length === token.length) return true;
        if (matchFrom(start + length, tokenIndex + length)) return true;
        if (text[start + length] !== token[tokenIndex + length]) break;
        length++;
      }
    }

    return false;
  };

  return matchFrom(0, 0);
};
